import React from 'react';
import { AlertTriangle, CheckCircle2, Clock, FileText, Loader2, X } from 'lucide-react';
import { PORTAL_BTN } from '../../arkorbit-ui/portalTheme';
import { portalFallanalyseSurfaceClass } from '../portalAppearance';
import { usePortalAppearance } from '../portalAppearanceContext';
import { PORTAL_NAV_LITIGATION_LABEL } from '../portalNavigation';
import type { PortalDocumentOpenFields } from '../portalDocuments';
import { PORTAL_SHELL } from '../portalTheme';
import { JudicatureDemoJobCard } from './JudicatureDemoJobCard';

/** Gleiche Fläche wie Kalender und Kanzlei & Zugänge: schwarz, feine lila Kante. */
const FALLANALYSE_GROUND = 'bg-[#07070a]';
const FALLANALYSE_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';

export interface LitigationSummary {
  claims_gefunden?: number;
  davon_kritisch?: number;
  davon_stark?: number;
  davon_mittel?: number;
}

export interface LitigationUploadResult {
  type: 'success' | 'error';
  message?: string;
  pdfUrl?: string;
  summary?: LitigationSummary;
  pdfLinkExpiresAt?: string;
  pdfGenerationNote?: string;
}

/** Nur diese Komponente aktualisiert jede Sekunde – verhindert Flackern im restlichen Portal */
export function LitigationPdfCountdown({
  expiresAtIso,
  tone = 'amber',
}: {
  expiresAtIso?: string;
  /** Fallanalyse = Violett. Vertragsprüfung behält den bisherigen Hinweis-Ton. */
  tone?: 'amber' | 'violet';
}) {
  const endMs = React.useMemo(() => {
    if (expiresAtIso) {
      const t = new Date(expiresAtIso).getTime();
      if (!Number.isNaN(t)) return t;
    }
    return Date.now() + 30 * 60 * 1000;
  }, [expiresAtIso]);

  const [remainingSec, setRemainingSec] = React.useState(() =>
    Math.max(0, Math.floor((endMs - Date.now()) / 1000)),
  );

  React.useEffect(() => {
    setRemainingSec(Math.max(0, Math.floor((endMs - Date.now()) / 1000)));
  }, [endMs]);

  React.useEffect(() => {
    const id = window.setInterval(() => {
      setRemainingSec(Math.max(0, Math.floor((endMs - Date.now()) / 1000)));
    }, 1000);
    return () => window.clearInterval(id);
  }, [endMs]);

  if (remainingSec <= 0) return null;

  const mm = Math.floor(remainingSec / 60);
  const ss = remainingSec % 60;

  const violet = tone === 'violet';

  return (
    <div
      className={
        violet
          ? `flex items-center gap-2 px-3 py-2 text-sm text-slate-200 [contain:layout] ${FALLANALYSE_EDGE}`
          : 'flex items-center gap-2 rounded-xl bg-slate-800/80 border border-slate-600/50 px-3 py-2 text-sm text-slate-200 [contain:layout]'
      }
      style={{ minHeight: '2.75rem' }}
    >
      <Clock
        className={`w-4 h-4 flex-shrink-0 pointer-events-none ${violet ? 'text-violet-300' : 'text-amber-400'}`}
        aria-hidden
      />
      <p className="leading-snug m-0 flex-1 min-w-0">
        Empfohlen: PDF innerhalb von <strong className="text-white">30 Min.</strong> öffnen — noch ca.{' '}
        <span
          className={`inline-block min-w-[5.25ch] tabular-nums font-semibold text-right ${
            violet ? 'text-violet-200' : 'text-amber-300'
          }`}
        >
          {String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
        </span>
      </p>
    </div>
  );
}

export interface LitigationViewProps {
  textMinChars: number;
  uploadMode: 'pdf' | 'text';
  onUploadModeChange: (mode: 'pdf' | 'text') => void;
  files: File[];
  onFilesChange: (files: File[]) => void;
  schriftsatzText: string;
  onSchriftsatzTextChange: (text: string) => void;
  optIn: boolean;
  onOptInChange: (checked: boolean) => void;
  validationError: string;
  onValidationErrorClear: () => void;
  uploading: boolean;
  uploadResult: LitigationUploadResult | null;
  onUpload: () => void;
  customerId: string;
  customerStatus?: string;
  customerEmail?: string;
  onDocumentReady: (fields: PortalDocumentOpenFields) => void | Promise<void>;
}

export const LitigationView: React.FC<LitigationViewProps> = ({
  textMinChars,
  uploadMode,
  onUploadModeChange,
  files,
  onFilesChange,
  schriftsatzText,
  onSchriftsatzTextChange,
  optIn,
  onOptInChange,
  validationError,
  onValidationErrorClear,
  uploading,
  uploadResult,
  onUpload,
  customerId,
  customerStatus,
  customerEmail,
  onDocumentReady,
}) => {
  const fallanalyseLight = portalFallanalyseSurfaceClass(usePortalAppearance());
  return (
  <div
    className={`flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-y-auto ${FALLANALYSE_GROUND} ${FALLANALYSE_EDGE} ${fallanalyseLight}`}
    data-testid="portal-fallanalyse"
  >
    <header className="flex flex-shrink-0 items-start gap-3 border-b border-violet-400/30 px-3.5 py-3">
      <div className="min-w-0">
        <h1 className="m-0 flex items-center gap-2 text-sm font-semibold text-white">
          <FileText className="h-4 w-4 text-violet-300" aria-hidden />
          {PORTAL_NAV_LITIGATION_LABEL}
        </h1>
        <p className="m-0 mt-0.5 text-[11px] leading-relaxed text-slate-500">
          Gegnerischen Schriftsatz prüfen lassen — Strategie-PDF als Entwurf zur anwaltlichen Prüfung.
        </p>
      </div>
    </header>
    <div className="flex min-h-0 flex-1 flex-col gap-3 px-3 py-3">

    <JudicatureDemoJobCard
      customerId={customerId}
      customerStatus={customerStatus}
      customerEmail={customerEmail}
      onDocumentReady={onDocumentReady}
    />

    {uploadResult?.type === 'success' && (
      <div className="space-y-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-emerald-400 font-bold">Analyse abgeschlossen!</p>
            {uploadResult.summary && (
              <div className="flex flex-wrap gap-3 mt-2">
                {uploadResult.summary.claims_gefunden !== undefined && (
                  <span className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded-lg">
                    {uploadResult.summary.claims_gefunden} Behauptungen
                  </span>
                )}
                {(uploadResult.summary.davon_kritisch ?? 0) > 0 && (
                  <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-1 rounded-lg font-bold">
                    {uploadResult.summary.davon_kritisch} Kritisch
                  </span>
                )}
                {(uploadResult.summary.davon_stark ?? 0) > 0 && (
                  <span className="text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2 py-1 rounded-lg font-bold">
                    {uploadResult.summary.davon_stark} Stark
                  </span>
                )}
                {(uploadResult.summary.davon_mittel ?? 0) > 0 && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-1 rounded-lg">
                    {uploadResult.summary.davon_mittel} Mittel
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {uploadResult.pdfUrl ? (
          <>
            <a
              href={uploadResult.pdfUrl}
              target="_blank"
              rel="noreferrer"
              download
              className={`flex items-center justify-center gap-2 w-full rounded-lg py-2.5 text-sm font-medium ${PORTAL_BTN.primary}`}
            >
              <FileText className="w-4 h-4" /> Strategie-PDF herunterladen
            </a>
            <LitigationPdfCountdown expiresAtIso={uploadResult.pdfLinkExpiresAt} tone="violet" />
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-slate-400 text-xs">
              Das Strategie-PDF laden Sie hier herunter, sobald der Server den Link erzeugt hat.
              Bis dahin nutzen Sie die Kennzahlen oben; technische Hinweise siehe unten.
            </p>
            <LitigationPdfCountdown expiresAtIso={uploadResult.pdfLinkExpiresAt} tone="violet" />
            {uploadResult.pdfGenerationNote && (
              <p className={`text-xs break-words p-2 ${FALLANALYSE_EDGE} ${PORTAL_SHELL.textSubtle}`}>
                {uploadResult.pdfGenerationNote}
              </p>
            )}
          </div>
        )}
      </div>
    )}

    {uploadResult?.type === 'error' && (
      <div className="flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-5">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-red-400 font-bold">Analyse fehlgeschlagen</p>
          <p className="text-slate-300 text-sm mt-1">
            {uploadResult.message || 'Bitte versuchen Sie es erneut oder kontaktieren Sie office@arkorbit.at'}
          </p>
        </div>
      </div>
    )}

    <div className={`flex min-h-0 flex-1 flex-col space-y-5 p-5 ${FALLANALYSE_EDGE}${uploading ? ' opacity-90 pointer-events-none' : ''}`}>
      <div
        className={`flex gap-0.5 p-0.5 ${FALLANALYSE_EDGE}`}
        role="tablist"
        aria-label="Eingabe für die Fallanalyse"
      >
        <button
          type="button"
          role="tab"
          aria-selected={uploadMode === 'pdf'}
          onClick={() => {
            onUploadModeChange('pdf');
            onValidationErrorClear();
          }}
          className={`flex-1 text-[12px] font-medium py-1.5 rounded-md transition-colors ${
            uploadMode === 'pdf'
              ? 'bg-violet-500/15 text-white ring-1 ring-violet-400/30'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          PDF hochladen
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={uploadMode === 'text'}
          onClick={() => {
            onUploadModeChange('text');
            onValidationErrorClear();
          }}
          className={`flex-1 text-[12px] font-medium py-1.5 rounded-md transition-colors ${
            uploadMode === 'text'
              ? 'bg-violet-500/15 text-white ring-1 ring-violet-400/30'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          Text eingeben
        </button>
      </div>

      {uploadMode === 'pdf' ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Dokumente (PDF) · bis zu 10
            </label>
            {files.length > 0 && (
              <span className="text-xs text-violet-200 font-medium">
                {files.length} Datei{files.length > 1 ? 'en' : ''} ausgewählt
              </span>
            )}
          </div>

          {files.length > 0 ? (
            <div className="space-y-2">
              {files.map((f, i) => (
                <div
                  key={`${f.name}-${f.size}-${i}`}
                  className={`flex items-center justify-between px-3 py-2 ${FALLANALYSE_EDGE}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-violet-300 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate max-w-[220px]">{f.name}</p>
                      <p className="text-slate-500 text-[10px]">{(f.size / 1024 / 1024).toFixed(1)} MB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onFilesChange(files.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-300 ml-2 flex-shrink-0"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {files.length < 10 && (
                <label className="flex items-center justify-center gap-2 border border-dashed border-violet-400/30 hover:border-violet-400/60 rounded-lg bg-[#07070a] py-2.5 cursor-pointer text-slate-400 text-xs transition-colors">
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    className="hidden"
                    onChange={(e) =>
                      onFilesChange([...files, ...Array.from(e.target.files || [])].slice(0, 10))
                    }
                  />
                  <FileText className="w-3.5 h-3.5" />
                  Weiteres Dokument hinzufügen ({10 - files.length} verbleibend)
                </label>
              )}
            </div>
          ) : (
            <label className="flex min-h-[12rem] flex-1 flex-col items-center justify-center border border-dashed border-violet-400/30 hover:border-violet-400/60 rounded-lg bg-[#07070a] p-8 cursor-pointer transition-colors">
              <input
                type="file"
                accept=".pdf"
                multiple
                className="hidden"
                onChange={(e) => onFilesChange(Array.from(e.target.files || []).slice(0, 10))}
              />
              <FileText className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <p className="text-slate-200 font-medium text-sm">PDFs hier ablegen oder klicken</p>
              <p className={`text-xs mt-1 ${PORTAL_SHELL.textMuted}`}>Bis zu 10 Dateien · je max. 20 MB</p>
            </label>
          )}
        </div>
      ) : (
        <div>
          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-2">
            Schriftsatztext (direkt)
          </label>
          <p className="text-slate-500 text-xs mb-2">
            Wenn kein PDF vorliegt — mindestens {textMinChars} Zeichen.
          </p>
          <textarea
            value={schriftsatzText}
            onChange={(e) => {
              onSchriftsatzTextChange(e.target.value);
              onValidationErrorClear();
            }}
            rows={10}
            disabled={uploading}
            placeholder="Klage des Dr. Max Mustermann gegen die Muster GmbH, FN 123456a. Beklagte: Alpentech GmbH. Klagebegehren EUR 850,00."
            className="w-full min-h-[12rem] flex-1 resize-y rounded-lg border border-violet-400/30 bg-[#07070a] px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400/40"
          />
          <p className="text-slate-500 text-[10px] mt-1 text-right">
            {schriftsatzText.trim().length} / {textMinChars} Zeichen min.
          </p>
        </div>
      )}

      <div
        className={`p-4 ${FALLANALYSE_EDGE} ${!optIn && validationError ? 'border-red-500/40' : ''}`}
      >
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={optIn}
            onChange={(e) => {
              onOptInChange(e.target.checked);
              if (e.target.checked) onValidationErrorClear();
            }}
            className="mt-0.5 accent-violet-500"
          />
          <span className="text-slate-300 text-sm leading-relaxed">
            Ich bestätige, dass ich berechtigt bin, dieses Dokument hochzuladen, und stimme der KI-Analyse zu. Meine
            Kanzlei kann Texte vorab anonymisieren (z. B. „Mandant A"). ARKORBIT pseudonymisiert vor der
            KI-Verarbeitung ein weiteres Mal.
          </span>
        </label>
      </div>

      {validationError && (
        <div className="flex items-start gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/25 rounded-xl px-3 py-2.5">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{validationError}</span>
        </div>
      )}

      <button
        type="button"
        onClick={onUpload}
        disabled={uploading}
        className={`w-full rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-2 ${
          uploading ? PORTAL_BTN.primaryDisabled : PORTAL_BTN.primary
        }`}
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Analyse läuft — bitte warten (bis zu 5 Min.)…
          </>
        ) : (
          <>
            <FileText className="w-4 h-4" /> Analyse starten
          </>
        )}
      </button>
    </div>
    </div>
  </div>
  );
};
